import { useCallback, useEffect, useRef, useState } from 'react'
import { useManualQuery } from 'graphql-hooks'
import uniqBy from 'lodash/uniqBy'
import sortBy from 'lodash/sortBy'
import { getUsersFilteredByStatus } from '../graphql/custom-queries'
import { IExportFilterProps } from '../components/reports/types'

interface IGetUsersFilteredByStatusResponse {
  getUsersFiltered: {
    users: IExportFilterProps[]
  }
}

interface UseUsersForFilterReturn {
  usersForFilter: IExportFilterProps[]
  loadingUsersForFilter: boolean
}

/**
 * Custom hook for fetching users based on selected user statuses for report filters.
 * Handles multiple statuses by fetching in parallel and merging results.
 * Includes race condition prevention for rapid status changes.
 *
 * @param userStatuses - Array of user statuses to fetch (e.g., ['ACTIVE'], ['DELETED', 'INACTIVE'])
 */
export const useUsersForFilter = (userStatuses: string[] | undefined): UseUsersForFilterReturn => {
  const [usersForFilter, setUsersForFilter] = useState<IExportFilterProps[]>([])
  const [loadingUsersForFilter, setLoadingUsersForFilter] = useState(false)
  const [getUsersFilteredByStatusQuery] = useManualQuery<IGetUsersFilteredByStatusResponse, { status: string }>(getUsersFilteredByStatus)

  // Ref to track the latest fetch request and prevent race conditions
  const fetchUsersRequestId = useRef(0)

  // Fetch users for the filter dropdown based on selected user statuses
  const fetchUsersForFilter = useCallback(async (statuses: string[]) => {
    // If no statuses selected, default to ACTIVE
    const statusesToFetch = statuses.length > 0 ? statuses : ['ACTIVE']

    // Increment request ID to track latest request
    const currentRequestId = ++fetchUsersRequestId.current

    setLoadingUsersForFilter(true)
    // Clear users immediately so dropdown doesn't show stale data
    setUsersForFilter([])

    try {
      // Fetch users for each status and merge results
      const userPromises = statusesToFetch.map(status =>
        getUsersFilteredByStatusQuery({ variables: { status } })
      )

      const results = await Promise.all(userPromises)

      // Check if this is still the latest request (prevents race conditions)
      if (currentRequestId !== fetchUsersRequestId.current) {
        return
      }

      // Merge and deduplicate users from all statuses
      const allUsers: IExportFilterProps[] = []
      for (const result of results) {
        if (result.data?.getUsersFiltered?.users) {
          allUsers.push(...result.data.getUsersFiltered.users)
        }
      }

      // Remove duplicates by id and sort by name
      const uniqueUsers = sortBy(uniqBy(allUsers, 'id'), [(u) => u.name.toLowerCase()])
      setUsersForFilter(uniqueUsers)
    } catch (error) {
      // On error, keep existing users to avoid breaking the UI
      console.error('Failed to fetch users for filter:', error)
    } finally {
      if (currentRequestId === fetchUsersRequestId.current) {
        setLoadingUsersForFilter(false)
      }
    }
  }, [getUsersFilteredByStatusQuery])

  // Fetch users when userStatuses changes
  useEffect(() => {
    fetchUsersForFilter(userStatuses || ['ACTIVE'])
  }, [userStatuses, fetchUsersForFilter])

  return {
    usersForFilter,
    loadingUsersForFilter,
  }
}
